import {
  Box,
  Button,
  type ButtonProps,
  Text,
  Tooltip,
  useToast,
} from "@prismicio/editor-ui"
import {
  type ElementRef,
  forwardRef,
  type ReactNode,
  useMemo,
  useState,
} from "react"

import {
  LocaleSelection,
  type SelectedLocale,
} from "../../app/components/locales/LocaleSelection"
import { useRedirect, type UseRedirectOptions } from "../../app/routing"
import { useSubscriptionQuery } from "../billing/useSubscription"
import * as localeUtils from "./localeUtils"
import { useDefaultLocalesQuery } from "./useDefaultLocales"
import {
  useAddRepositoryLocaleMutation,
  useRepositoryLocalesQuery,
} from "./useRepositoryLocales"

export function AddLocaleForm() {
  const { data: defaultLocales } = useDefaultLocalesQuery()
  const { data: repositoryLocales } = useRepositoryLocalesQuery()
  const { data: subscription } = useSubscriptionQuery()

  const { showToast } = useToast()
  const addRepositoryLocaleMutation = useAddRepositoryLocaleMutation({
    onSuccess: (repositoryLocale) => {
      showToast({ title: `Locale "${repositoryLocale.label}" added` })
      setSelectedLocale(undefined)
    },
    onError: (repositoryLocaleUpsert) => {
      showToast({
        title: `Unable to add locale "${repositoryLocaleUpsert.id}"`,
        icon: "alert",
      })
    },
  })

  const availableLocales = useMemo(
    () => localeUtils.getAvailableLocales(defaultLocales, repositoryLocales),
    [defaultLocales, repositoryLocales],
  )

  const [selectedLocale, setSelectedLocale] = useState<SelectedLocale>()

  const masterLocale = localeUtils.getMasterLocale(repositoryLocales)
  const redirect = useRedirect(masterLocale === undefined, useRedirectOptions)
  if (redirect) return null

  const canAddLocale = localeUtils.canAddLocale(
    subscription.limits.locales,
    repositoryLocales.length,
  )

  return (
    <AddLocaleFormTemplate
      localeSelect={
        <LocaleSelection
          locale={selectedLocale}
          onLocaleChange={setSelectedLocale}
          locales={availableLocales}
          addCustomLocaleEnabled
          isEnabled={canAddLocale && !addRepositoryLocaleMutation.isPending}
        />
      }
      addLocaleButton={
        <Tooltip
          align="end"
          content="Your current plan doesn't allow you to add more locales"
          visible={!canAddLocale}
        >
          <AddLocaleFormTemplateAddLocaleButton
            disabled={
              selectedLocale === undefined ||
              // TODO(PBD-1869): Remove this condition when the `LocaleSelection`
              // component will no longer allow selecting an already added custom
              // locale.
              repositoryLocales.some(
                (repositoryLocale) => repositoryLocale.id === selectedLocale.id,
              )
            }
            loading={addRepositoryLocaleMutation.isPending}
            onClick={() => {
              if (selectedLocale === undefined) return
              addRepositoryLocaleMutation.mutate({
                ...selectedLocale,
                isMaster: false,
              })
            }}
          />
        </Tooltip>
      }
    />
  )
}

interface AddLocaleFormTemplateProps {
  localeSelect: ReactNode
  addLocaleButton: ReactNode
}

function AddLocaleFormTemplate(props: AddLocaleFormTemplateProps) {
  const { localeSelect, addLocaleButton } = props
  return (
    <Box alignItems="flex-end" gap={16}>
      <Box flexDirection="column" flexGrow={1} gap={4}>
        <Text variant="emphasized" component="label">
          Locale
        </Text>
        {localeSelect}
      </Box>
      {addLocaleButton}
    </Box>
  )
}

const AddLocaleFormTemplateAddLocaleButton = forwardRef<
  ElementRef<typeof Button>,
  ButtonProps
>(function AddLocaleFormTemplateAddLocaleButton(props, ref) {
  return (
    <Button {...props} color="grey" ref={ref} size="large">
      Add locale
    </Button>
  )
})

export function AddLocaleFormSkeleton() {
  return (
    <AddLocaleFormTemplate
      localeSelect={
        <LocaleSelection
          locale={undefined}
          onLocaleChange={() => {}}
          locales={[]}
          isEnabled={false}
        />
      }
      addLocaleButton={<AddLocaleFormTemplateAddLocaleButton disabled />}
    />
  )
}

const useRedirectOptions: UseRedirectOptions = {
  to: "/onboarding",
  title: "Forbidden",
  subtitle:
    "You must choose a master locale to access this page. You have been redirected to the onboarding page.",
}
